<script lang="ts">
	import { onMount, tick } from 'svelte';
	import { handleStoreError } from '../../stores/GeneralStore';

	interface Props {
		id?: string | null;
		value?: string | null;
		focusOnStart?: boolean;
		placeholder?: string | null;
		propertyName: string;
		positionDesign?: boolean;
		disabled?: boolean;
		rightAlign?: boolean;
		callFunction?: ((object: any) => Promise<any>) | null;
		errors?: any;
		tabSwitcher?: boolean;
		wrappInOptionalParameter?: boolean;
		onBlur?: (e: { currentTarget: HTMLInputElement }) => void;
		onInput?: (e: Event & { currentTarget: HTMLInputElement }) => void;
		onChange?: (e: Event & { currentTarget: HTMLInputElement }) => void;
		onKeyup?: (e: KeyboardEvent & { currentTarget: HTMLInputElement }) => void;
	}

	let {
		id = null,
		value = $bindable(''),
		focusOnStart = false,
		placeholder = null,
		propertyName,
		positionDesign = false,
		disabled = false,
		rightAlign = false,
		callFunction = null,
		errors = {},
		tabSwitcher = false,
		wrappInOptionalParameter = false,
		onBlur = undefined,
		onChange = undefined,
		onInput = undefined,
		onKeyup = undefined
	}: Props = $props();

	let regexMatch = new RegExp('^([\\w\\-\\.@]+)$');

	let element: HTMLInputElement | undefined = $state();

	onMount(async () => {
		if (element && focusOnStart) {
			await tick();
			element.focus();
			element.select();
		}
	});

	function handleBeforeInput(event: InputEvent) {
		const target = event.target as HTMLInputElement;

		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart || 0) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd || 0);

		//Check if we do match the number
		if (nextVal.length > 0 && nextVal.match(regexMatch) == null) {
			event.preventDefault();
			return;
		}
	}

	function handleOnInput(event: Event) {
		if (callFunction) return;
		const target = event.target as HTMLInputElement;
		value = target.value;
	}

	async function handleOnChange(event: Event) {
		const target = event.target as HTMLInputElement;

		if (callFunction) {
			try {
				const dynamicObject: any = {};
				//Check if we should wrapp the property in an optional
				if (wrappInOptionalParameter) {
					//Send null if the value is empty
					const usedValue = target.value ? target.value : null;

					dynamicObject[propertyName] = { value: usedValue };
				} else {
					dynamicObject[propertyName] = target.value;
				}

				await callFunction(dynamicObject);

				value = target.value;
			} catch (error) {
				//Check if we have an error type
				handleStoreError('general.patch_failed', error);

				//We must reset the value
				target.value = value || '';
			}
		}
	}

	function handleOnPaste(event: ClipboardEvent) {
		event.preventDefault();

		if (!element) return;

		let trimmedValue = event.clipboardData?.getData('text').trim() || '';

		element.value = trimmedValue;

		element.dispatchEvent(
			new Event('change', {
				bubbles: true,
				cancelable: true
			})
		);
	}
</script>

<div class:positionDesign>
	<input
		{id}
		data-testid={propertyName}
		type="text"
		autocomplete="off"
		{disabled}
		class:switcher={tabSwitcher}
		class:text-right={rightAlign}
		onbeforeinput={handleBeforeInput}
		onchange={(e) => {
			onChange?.(e);
			handleOnChange(e);
		}}
		oninput={(e) => {
			onInput?.(e);
			handleOnInput(e);
		}}
		onkeyup={onKeyup}
		onblur={onBlur}
		onpaste={handleOnPaste}
		onfocus={(e) => e.currentTarget.select()}
		bind:this={element}
		{value}
		{placeholder}
	/>

	{#if errors[propertyName]}
		<p class="text-red dark:text-orange text-sm">{errors[propertyName]}</p>
	{/if}
</div>
